import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button, Card, CardHeader, CardBody, Container, Row, Col, FormGroup, Input, Table } from 'reactstrap';
import { ApiService } from 'services';
import Header from 'components/Headers/Header';
import Loader from 'components/Loader';
import { titleCase } from 'helpers';
import { useConfirm } from 'hooks';
import config from 'config';
import ConfirmModal from 'components/ConfirmModal';
import Logs from 'components/Logs';
import { BSO } from './types';

interface Data {
  bill: BSO
  billDetails: BSODetail[]
}

interface BSODetail {
  id: number
  afiliado: string
  afiliado_id: string
  cant_sesiones: number
  monto: number
  solicitud: number
}

export default function Audit() {
  const [isLoading, setIsLoading] = useState(false);
  const [bill, setBill] = useState<Data['bill']>();
  const [details, setDetails] = useState<Data['billDetails']>();

  const [observation, setObservation] = useState('');
  const [isEmpty, setIsEmpty] = useState(false);
  const [showObservationForm, setShowObservationForm] = useState(false);
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (!id) return;

    setIsLoading(true);

    ApiService.getOne('submission-orders', id)
      .then(res => {
        setBill(res.bill);
        setDetails(res.billDetails);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const showObservation = () => {
    setShowObservationForm(true);

    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);
  };

  const submitObservation = () => {
    if (!observation || observation === '') {
      setIsEmpty(true);
      return;
    }

    setIsLoading(true);
    setIsEmpty(false);

    const data = {
      status: 'observed',
      observation,
    };

    ApiService.update('submission-orders', id, data)
      .then(() => {
        navigate('/admin/pedidos-presentacion', {
          state: { success: true },
        });
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const cancelObservation = () => {
    setShowObservationForm(false);
    setObservation('');
    setIsEmpty(false);
  };

  const submitApproval = async (confirmed = false) => {
    setIsLoading(true);

    if (!confirmed) {
      return setConfirm(true, '¿Estás seguro que deseas aceptar esta solicitud?', 'accept');
    }

    const data = {
      status: 'approved',
    };

    ApiService.update('submission-orders', id, data)
      .then(() => {
        navigate('/admin/pedidos-presentacion', {
          state: { success: true },
        });
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onConfirm = (action: string) => {
    setConfirm(false);
    if (action === 'observe') {
      return submitObservation();
    }
    if (action === 'accept') {
      return submitApproval(true);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <ConfirmModal
          isOpen={confirm}
          title={confirmTitle}
          action={confirmAction}
          onClose={ () => setConfirm(false) }
          onConfirm={ (action: string) => onConfirm(action) }
        />
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col xs="8">
                <h3 className="mb-0">
                  <span className='mr-2'>Auditar Factura</span>
                </h3>
              </Col>
              <Col className="text-right" xs="4">
                <Link to='/admin/pedidos-presentacion'>
                  <Button color="primary" size="md">
                    Volver
                  </Button>
                </Link>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {
              isLoading
                ? <Loader />
                : <>
                    <Row className='mb-4'>
                      <Col lg='3'>Matriculado</Col>
                      <Col lg='9'>
                        {
                          bill?.matriculate
                            // eslint-disable-next-line max-len
                            ? titleCase(`${bill.matriculate.apellido} ${bill.matriculate.nombre} (Mat. ${ bill.matriculate.matricula })`)
                            : '-' }</Col>
                    </Row>
                    <Row className='mb-4'>
                      <Col lg='3'>Delegación</Col>
                      <Col lg='9'>{ bill?.matriculate ? bill?.matriculate.delegacion : '-' }</Col>
                    </Row>
                    <Row className='mb-4'>
                      <Col lg='3'>Obra Social</Col>
                      <Col lg='9'>{ bill?.ooss ? bill?.ooss.nombre : '-' }</Col>
                    </Row>
                    <Row className='mb-4'>
                      <Col lg='3'>Remito</Col>
                      <Col lg='9'>{ bill?.remito_id ? bill?.remito_id : '-' }</Col>
                    </Row>
                    <Row className='mb-4'>
                      <Col lg='3'>Periodo</Col>
                      <Col lg='9'>{ bill?.monthPeriod }/{ bill?.yearPeriod }</Col>
                    </Row>
                    <Row className='mb-4'>
                      <Col lg='3'>Fecha de solicitud</Col>
                      <Col lg='9'>
                        {
                          bill?.createdAt
                            ? new Date(bill?.createdAt).toLocaleDateString('es-AR', { dateStyle: 'short' })
                            : null
                        }
                      </Col>
                    </Row>
                    <Row className='mb-4'>
                      <h3>Detalle</h3>
                      <Table>
                        <thead>
                          <tr>
                            <th>N° Afiliado</th>
                            <th>Afiliado</th>
                            <th>Solicitud</th>
                            <th># Sesiones</th>
                            <th>Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            details?.map(bso => (
                              <tr key={`bso_${bso.id}`}>
                                <td>{ bso.afiliado_id }</td>
                                <td>{ bso.afiliado }</td>
                                <td>{ bso.solicitud }</td>
                                <td>{ bso.cant_sesiones }</td>
                                <td>{ bso.monto }</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </Table>
                    </Row>
                    {
                      bill?.attachs && Array.isArray(bill?.attachs) && bill?.attachs.length
                        ? bill?.attachs.map(({ type, url }, idx) =>
                          <Row className="mb-4" key={`attach_${idx}`}>
                            <Col md='3'>{ type === 'factura' ? 'Factura' : 'Nota de Crédito' }</Col>
                            <Col md='9'>
                              <Button
                                color="primary"
                                onClick={ () => window.open(`${config.baseUrl}/${url}`, '_blank') }
                              >
                                Ver adjunto
                              </Button>
                            </Col>
                          </Row>,
                        )
                        : null
                    }
                    {
                      bill?.observations && Array.isArray(bill.observations) && bill.observations.length
                        ? <Row>
                            <Col lg="12">
                              <hr className="my-4" />
                              <h6 className="heading-small text-muted mb-4">Observaciones</h6>
                              <div className="pl-lg-4 mb-4">
                                {
                                  bill.observations.map(obs =>
                                    <p className='mt-2' key={obs.id}>
                                      <span><strong>{new Date(obs.createdAt).toLocaleString('es')}</strong> - </span>
                                      { obs.text }
                                    </p>,
                                  )
                                }
                              </div>
                            </Col>
                          </Row>
                        : null
                    }
                    <Row className={!showObservationForm ? 'hidden' : '' }>
                      <Col lg="12">
                        <FormGroup>
                          <Input autoComplete='off'
                            className={isEmpty ? `required` : 'form-control-alternative'}
                            type="textarea"
                            rows="4"
                            name="observation"
                            placeholder="Agregar observación"
                            onChange={ event => setObservation(event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className={!showObservationForm ? 'hidden' : '' }>
                      <div className="update ml-auto mr-auto">
                        <Button
                          className="btn-round"
                          color="success"
                          disabled={ isLoading }
                          onClick={ submitObservation }
                        >
                        Aceptar
                        </Button>
                        <Button className="btn-round" color="error" size="md" onClick={ cancelObservation }>
                        Cancelar
                        </Button>
                      </div>
                    </Row>
                    <Row className={ showObservationForm ? 'hidden' : '' }>
                      <div className="update ml-auto mr-auto">
                        <Button
                          className="btn-round"
                          color="primary"
                          disabled={ isLoading }
                          onClick={ () => showObservation() }
                        >
                        Observar Factura
                        </Button>
                        <Button
                          disabled={ isLoading }
                          className="btn-round"
                          color="success"
                          size="md"
                          onClick={ () => submitApproval() }
                        >
                        Aprobar Factura
                        </Button>
                      </div>
                    </Row>
                    {
                      id
                        ? <Logs resource="bso" id={ parseInt(id) } />
                        : null
                    }
                  </>
            }
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
